import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { countVisibleModalLayers } from 'src/app/shared/focus-navigation/form-focus-navigation.utils';
import { showFormAlertToast } from 'src/app/shared/components/form-alerts/form-alerts.toast';

const MODAL_NAV_WARNING_ID = 'modal-navigation-blocked';
const MODAL_NAV_WARNING_MESSAGE = 'Please close the open modal before navigating to another page.';
const WARNING_THROTTLE_MS = 1500;

@Injectable({ providedIn: 'root' })
export class ModalNavigationService {
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private lastWarningAt = 0;

  hasOpenModal(): boolean {
    return countVisibleModalLayers() > 0;
  }

  normalizePath(url: string): string {
    return url.split('?')[0].replace(/\/+$/, '') || '/';
  }

  shouldBlockNavigation(nextUrl: string, currentUrl?: string): boolean {
    if (!this.hasOpenModal()) {
      return false;
    }

    const fromUrl = currentUrl ?? this.router.url;
    return this.normalizePath(nextUrl) !== this.normalizePath(fromUrl);
  }

  warnNavigationBlocked(): void {
    const now = Date.now();
    if (now - this.lastWarningAt < WARNING_THROTTLE_MS) {
      return;
    }

    this.lastWarningAt = now;
    showFormAlertToast(this.messageService, {
      id: MODAL_NAV_WARNING_ID,
      visible: true,
      variant: 'warning',
      message: MODAL_NAV_WARNING_MESSAGE,
    });
  }

  /** Returns true when navigation may proceed. */
  ensureNavigationAllowed(nextUrl: string, currentUrl?: string): boolean {
    if (!this.shouldBlockNavigation(nextUrl, currentUrl)) {
      return true;
    }

    this.warnNavigationBlocked();
    return false;
  }

  /** Navigate after bootstrap / PrimeNG modal layers finish closing (avoids false guard blocks). */
  navigateWhenModalLayersClear(navigate: () => void | Promise<unknown>, maxWaitMs = 2000): void {
    const startedAt = Date.now();
    const attempt = (): void => {
      if (!this.hasOpenModal() || Date.now() - startedAt >= maxWaitMs) {
        void navigate();
        return;
      }
      requestAnimationFrame(attempt);
    };
    requestAnimationFrame(attempt);
  }
}
